import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getAssignmentRules,
  createAssignmentRule,
  updateAssignmentRule,
  deleteAssignmentRule,
  getTrainings,
} from '../../../services/trainingService';
import { EMPTY_TARGETS } from '../../../components/ui/AssignmentTargetPicker';
import { formatDate } from '../../../utils/Utils';

const useAssignmentRuleApi = (queryParams) => {
  const { t, i18n } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getAssignmentRules(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    try {
      await createAssignmentRule(toPayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch { return false; }
  }, []);

  const update = useCallback(async (id, formData) => {
    try {
      await updateAssignmentRule(id, toPayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch { return false; }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      for (const id of ids) await deleteAssignmentRule(id);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch { return false; }
  }, []);

  const triggerOptions = useMemo(() => [
    { id: 'new_employee', name: t('assignmentRule.trigger_new_employee') },
    { id: 'department_change', name: t('assignmentRule.trigger_department_change') },
    { id: 'title_change', name: t('assignmentRule.trigger_title_change') },
    { id: 'certificate_expiry', name: t('assignmentRule.trigger_certificate_expiry') },
    { id: 'date_based', name: t('assignmentRule.trigger_date_based') },
  ], [t]);

  const triggerLabel = (code) => triggerOptions.find((o) => o.id === code)?.name || code;

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name', label: t('assignmentRule.name'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'triggerType', label: t('assignmentRule.triggerType'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => triggerLabel(v),
      },
    },
    {
      name: 'trainingTitle', label: t('training.title'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '—' },
    },
    {
      name: 'dueDateDaysFromTrigger', label: t('assignmentRule.dueDateDays'),
      options: { filter: false, sort: true },
    },
    {
      name: 'createDate', label: t('training.createDate'),
      options: { filter: false, sort: true, customBodyRender: (v) => formatDate(v, i18n.language) },
    },
  ], [t, i18n.language, triggerLabel]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('assignmentRule.name'), type: 'text', grid: 12, required: true },
    { name: 'triggerType', label: t('assignmentRule.triggerType'), type: 'select', grid: 6, options: triggerOptions, required: true },
    { name: 'dueDateDaysFromTrigger', label: t('assignmentRule.dueDateDays'), type: 'number', grid: 6 },
    {
      name: 'trainingId', label: t('training.title'), type: 'asyncSelect', grid: 12, required: true,
      onSearch: async (q) => {
        const params = q
          ? { 'title[like]': q, 'status[eq]': 'published', pageSize: 20 }
          : { 'status[eq]': 'published', pageSize: 20 };
        const res = await getTrainings(params);
        const items = res.data?.data?.data || [];
        return items.map((i) => ({ id: i.id, name: i.title }));
      },
      onGetById: async (id) => {
        if (!id) return null;
        const res = await getTrainings({ pageSize: 200 });
        const items = res.data?.data?.data || [];
        const item = items.find((i) => i.id === id);
        return item ? { id: item.id, name: item.title } : { id, name: '' };
      },
    },
    {
      name: 'triggerConfigJson', label: t('assignmentRule.triggerConfig'), type: 'textarea', grid: 12,
      rows: 3, placeholder: '{}',
      description: t('assignmentRule.triggerConfigHint'),
    },
    { name: 'targets', label: t('assignmentRule.targets'), type: 'assignmentTargets', grid: 12 },
  ], [t, triggerOptions]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

// Convert form state to backend payload. AssignmentTargetPicker stores
// `targets` as an object; backend serializes it to TargetsJson server-side.
function toPayload(form) {
  return {
    name: (form.name || '').trim(),
    triggerType: form.triggerType,
    triggerConfigJson: (form.triggerConfigJson || '').trim() || '{}',
    targets: form.targets || { ...EMPTY_TARGETS, excludes: { ...EMPTY_TARGETS.excludes } },
    trainingId: form.trainingId,
    dueDateDaysFromTrigger: Number(form.dueDateDaysFromTrigger) || 30,
  };
}

// Hydrate a row coming from the API into a form-friendly shape (parse targetsJson).
export function rowToFormValues(row) {
  if (!row) return null;
  let targets = { ...EMPTY_TARGETS, excludes: { ...EMPTY_TARGETS.excludes } };
  try { targets = JSON.parse(row.targetsJson || '{}'); } catch { /* noop */ }
  return {
    id: row.id,
    name: row.name,
    triggerType: row.triggerType,
    triggerConfigJson: row.triggerConfigJson || '{}',
    targets,
    trainingId: row.trainingId,
    dueDateDaysFromTrigger: row.dueDateDaysFromTrigger,
  };
}

export default useAssignmentRuleApi;
